import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { VisitorPortalService } from "../../services/visitor-portal.service";
import { PortalVisitResponse } from "../../models/portal-visit.model";
import { ProgressSpinnerComponent } from "db-ui";
import { UserAvatarComponent } from "db-ui";
import { IUserViewAvatar } from "types";

@Component({
  selector: "db-visitor-portal-page",
  imports: [CommonModule, DatePipe, ProgressSpinnerComponent, UserAvatarComponent],
  templateUrl: "./visitor-portal-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisitorPortalPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly visitorPortalService = inject(VisitorPortalService);

  visit = signal<PortalVisitResponse | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);

  organizerUserView = computed(
    (): IUserViewAvatar => ({
      firstName: this.visit()?.organizer?.firstName,
      lastName: this.visit()?.organizer?.lastName,
    }),
  );

  ngOnInit(): void {
    const portalUuid = this.route.snapshot.paramMap.get("portalUuid");
    if (!portalUuid) {
      void this.router.navigate(["/not-available"]);
      return;
    }

    this.loadVisit(portalUuid);
  }

  private loadVisit(portalUuid: string): void {
    this.visitorPortalService.getVisitByPortalUuid(portalUuid).subscribe({
      next: (visit) => {
        this.visit.set(visit);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        void this.router.navigate(["/not-available"]);
      },
    });
  }

  getVisitorDisplayName(): string {
    const visitor = this.visit()?.visitor;
    if (!visitor) return "Visitor";

    const parts = [visitor.firstName, visitor.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Visitor";
  }

  getOrganizerDisplayName(): string {
    const organizer = this.visit()?.organizer;
    if (!organizer) return "";

    return `${organizer.firstName} ${organizer.lastName}`;
  }

  getFormattedAddress(): string {
    const address = this.visit()?.location?.officeAddress;
    if (!address) return "";

    const parts = [
      [address.street, address.streetNo].filter(Boolean).join(" "),
      [address.postalCode, address.city].filter(Boolean).join(" "),
      address.country,
    ].filter(Boolean);

    return parts.join(", ");
  }
}
